import { gql } from "graphql-request";

// Any changes in queries here should also be reflected in `src/models/assets.models.ts`
export const assets = {
  getAssets: gql`
    query Assets(
      $assetTypes: [AssetType!]
      $assetIds: [String!]
      $projectIds: [String!]
      $sourceArns: [String!]
      $size: Int
    ) {
      assets(
        assetTypes: $assetTypes
        assetIds: $assetIds
        projectIds: $projectIds
        sourceArns: $sourceArns
        size: $size
      ) {
        id
        createdBy
        createdDate
        lastModifiedBy
        lastModifiedDate
        type
        name
        status
        projectIds
        metadata
        source {
          arn
          tenantId
          tenantType
        }
        latestVersion {
          assetContentId
          createdBy
          createdDate
          version
        }
        isProxy
        parent {
          id
          createdBy
          createdDate
          lastModifiedBy
          lastModifiedDate
          type
          name
          status
          projectIds
          metadata
          source {
            arn
            tenantId
            tenantType
          }
          latestVersion {
            assetContentId
            createdBy
            createdDate
            version
          }
        }
      }
    }
  `,

  getAssetsContent: gql`
    query AssetsContent(
      $assetTypes: [AssetType!]
      $projectIds: [String!]
      $size: Int
    ) {
      assets(assetTypes: $assetTypes, projectIds: $projectIds, size: $size) {
        id
        assetContent {
          content
        }
      }
    }
  `,

  getAssetsByRelationships: gql`
    query AssetsByRelationships(
      $relationships: [String!]
      $otherAssetsIds: [String!]
      $assetsIds: [String!]
    ) {
      assetsByRelationships(
        relationships: $relationships
        otherAssetsIds: $otherAssetsIds
        assetsIds: $assetsIds
      ) {
        id
        createdBy
        createdDate
        lastModifiedBy
        lastModifiedDate
        type
        name
        status
        projectIds
        metadata
        source {
          arn
          tenantId
          tenantType
        }
        latestVersion {
          assetContentId
          createdBy
          createdDate
          version
        }
        isProxy
        parentId
        parent {
          id
          createdBy
          createdDate
          lastModifiedBy
          lastModifiedDate
          type
          name
          status
          projectIds
          metadata
          source {
            arn
            tenantId
            tenantType
          }
          latestVersion {
            assetContentId
            createdBy
            createdDate
            version
          }
        }
      }
    }
  `,

  getInlineHAQs: gql`
    query InlineHAQs($documentIds: [String!], $projectId: String, $size: Int) {
      inlineHAQs(
        documentIds: $documentIds
        projectId: $projectId
        size: $size
      ) {
        id
        organization {
          name
          shortName
          country
        }
        source {
          tenantId
          tenantType
        }
        name
        createdBy
        metadata {
          assetTypeSequenceNum
          assets {
            id
            name
          }
          highlightText
          highlightAreas {
            height
            left
            pageIndex
            top
            width
          }
          topic
          domain
          authoringTenantId
          referenceNumber
          invitedHealthAuthorityTenants {
            officialResponseRequested
            registeredInterest
            tenantId
          }
          completed
          attachments
          contentReference
          dueDate
          importType
          imported
        }
        status
        sentStatus
        formalResponse
        shareTracking {
          submissionDate
          type
          id
          tenantId
          responseDate
          status
        }
        assetContent {
          content
        }
        children {
          ... on Asset {
            id
            organization {
              name
              shortName
              country
            }
            source {
              tenantId
              tenantType
            }
            name
            createdBy
            createdDate
            metadata
            status
            type
            shareTracking {
              submissionDate
              type
              id
              tenantId
              responseDate
              status
            }
            assetContent {
              content
            }
          }
        }
      }
    }
  `,

  getStructuredDocument: gql`
    query StructuredDocument($assetId: String!) {
      structuredDocument(assetId: $assetId) {
        id
        createdBy
        createdDate
        lastModifiedBy
        lastModifiedDate
        type
        name
        status
        projectIds
        metadata
        sourceArn
        source {
          arn
          tenantId
          tenantType
        }
        latestVersion {
          createdBy
          createdDate
          assetContentId
          version
        }
        content {
          id
          createdBy
          createdDate
          assetId
          content
          contentSections {
            ...DocumentSectionFields
            sections {
              ...DocumentSectionFields
              sections {
                ...DocumentSectionFields
                sections {
                  ...DocumentSectionFields
                  sections {
                    ...DocumentSectionFields
                    sections {
                      ...DocumentSectionFields
                      sections {
                        ...DocumentSectionFields
                      }
                    }
                  }
                }
              }
            }
          }
          versions {
            createdBy
            createdDate
            assetContentId
            version
          }
        }
        shareTracking {
          id
          objectId
          tenantId
          type
          status
          submissionDate
          responseDate
        }
        submitHistory {
          tenantId
          recipient
          shareSentDate
          shareReceivedDate
          copySentDate
          copyReceivedDate
          errorDate
          status
        }
        organization {
          country
          type
          shortName
          tenantId
          id
          name
        }
        isProxy
      }
    }

    fragment DocumentSectionFields on StructuredDocumentSection {
      name
      version
      id
      status
      children
      childrenAssets {
        id
        name
        sourceArn
        status
        type
        metadata
        organization {
          type
          tenantId
          shortName
          name
          id
        }
        isProxy
        lastModifiedBy
        lastModifiedDate
        latestVersion {
          createdBy
          createdDate
          assetContentId
          version
        }
        parentId
        projectIds
        shareTracking {
          id
          objectId
          tenantId
          type
          status
          submissionDate
          responseDate
        }
        source {
          arn
          tenantId
          tenantType
        }
      }
    }
  `,

  getInvitation: gql`
    query Invitation($invitationId: ID!) {
      invitation(invitationId: $invitationId) {
        id
        createdBy
        createdDate
        lastModifiedBy
        lastModifiedDate
        type
        name
        status
        projectIds
        metadata {
          email
          version
          message
          sentDate
          tenantId
          dueDate
        }
        individualInvitations {
          id
          createdBy
          createdDate
          lastModifiedBy
          lastModifiedDate
          type
          name
          status
          projectIds
          metadata {
            email
            tenantId
          }
          source {
            arn
            tenantId
            tenantType
          }
          sourceArn
          isProxy
        }
        source {
          arn
          tenantId
          tenantType
        }
        sourceArn
        isProxy
      }
    }
  `,
};
